import * as React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

import { TicketStatusOption } from '@/constants/ticket-status-option.enum';
import { TicketChannelOption } from '@/constants/ticket-channel-option.enum';
import { SortOrder } from '@/constants/sort-order.enum';
import { connect } from '@/lib/redux/redux-connect';
import { updateParams } from '@/redux/app/actions/navigation';
import { Without } from '@/types/generic';
import { AppNavigatorParamsContext } from '@/lib/navigation';

import { UserInfoContext } from '../user/user-info';
import { UISelect } from '../ui-elements/UISelect';
import { UICheckbox } from '../ui-elements/UICheckbox';
import { UIDate } from '../ui-elements/UIDate';
import { UserSelect } from './UserSelect';

interface InboxFiltersState {
  visible: boolean;
  changed: boolean;
  status: string;
  userId: string;
  channel: string;
  unanswered: string;
  unaddressed: string;
  archived: string;
  order: string;
  from: string;
  to: string;
}

const InboxFiltersContainer = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')}
`;

const Connected = connect(null as void, { updateParams }, null, { forwardRef: true });

@Connected
export class InboxFilters extends Connected.Component<{}, Partial<InboxFiltersState>>() {
  static contextType = AppNavigatorParamsContext;

  declare context: React.ContextType<typeof AppNavigatorParamsContext>;

  handlers = {} as Record<keyof InboxFiltersState, (value: string, label?: string) => void>;

  params: InboxFilters['context'];

  constructor(props, params: React.ContextType<typeof AppNavigatorParamsContext>) {
    super(props);

    this.params = params;
    const { status, userId, channel, unanswered, unaddressed, archived, order, from, to } = params;
    this.state = {
      visible: false,
      changed: false,
      status,
      userId,
      channel,
      unanswered,
      unaddressed,
      archived,
      order,
      from,
      to,
    };
  }

  componentDidUpdate() {
    if (this.params === this.context) return;
    this.params = this.context;
    const updatedState = Object.keys(this.state)
      .reduce((acc, key) => Object.assign(acc, { [key]: this.context[key] }), {});
    this.setState(updatedState); // eslint-disable-line react/no-did-update-set-state
  }

  private getHandler(name: keyof InboxFiltersState) {
    let handler = this.handlers[name];
    if (!handler) {
      handler = (value, label) =>
        this.setState({ [name]: value, changed: label !== null } as unknown as InboxFiltersState);
      this.handlers[name] = handler;
    }
    return handler;
  }

  toggle = () => {
    const { visible } = this.state;
    this.setState({ visible: !visible });
  };

  toggleOff = () => {
    this.setState({ visible: false });
  };

  apply = () => {
    const { visible, changed, ...values } = this.state;
    this.redux.updateParams(values as any);
    this.setState({ changed: false, visible: false });
  };

  update(values?: Without<Partial<InboxFiltersState>, 'visible', 'changed'>) {
    this.redux.updateParams(values);
    this.setState({ changed: false, visible: false });
  }

  render() {
    const {
      visible, changed, status, userId, channel, unanswered, unaddressed, archived, order, from, to,
    } = this.state;
    return (
      <InboxFiltersContainer id="filters-and-sort" visible={visible}>
        <form id="filters-form">
          <div className="filters">
            <div className="selects">
              <UISelect name="status" prompt="Any" value={status} onChange={this.getHandler('status')}>
                <option value="">Any</option>
                {TicketStatusOption.mapValues((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </UISelect>
              <UserSelect onChange={this.getHandler('userId')} userId={userId} />
            </div>
            <div className="channels">
              <UISelect name="channel" prompt="Any Channel" value={channel} onChange={this.getHandler('channel')}>
                <option value="">Any Channel</option>
                {TicketChannelOption.mapValues((value) => (
                  <option key={value} value={value}>
                    {TicketChannelOption.human[value]}
                  </option>
                ))}
              </UISelect>
            </div>
            <UICheckbox name="unanswered" label="Unresponded Only" value={unanswered} onChange={this.getHandler('unanswered')} />
            <UICheckbox name="unaddressed" label="Unaddressed Only" value={unaddressed} onChange={this.getHandler('unaddressed')} />
            <UICheckbox name="archived" label="Show Closed Convos" value={archived} onChange={this.getHandler('archived')} />
            <div className="filter-date-buttons">
              <div className="from">
                <UIDate name="from" placeholder="from" value={from} onChange={this.getHandler('from')} />
              </div>
              <div className="to">
                <UIDate name="to" placeholder="to" value={to} onChange={this.getHandler('to')} />
              </div>
            </div>
          </div>
          <div className="sort">
            <UISelect name="order" prompt="Most Recent Activity" value={order} onChange={this.getHandler('order')}>
              {SortOrder.mapValues((value) => (
                <option key={value} value={value}>
                  {SortOrder.human[value]}
                </option>
              ))}
            </UISelect>
          </div>
          <button type="button" id="submit-filters" onClick={this.apply} className={classNames(changed && 'changed')}>Click to Apply Changes</button>
          <div className="filter-buttons">
            <UserInfoContext.Consumer>
              {({ lastUsedFilter }) => lastUsedFilter && (
                <div className="quick-filter-button">
                  <button type="button" className="ui-link" onClick={() => this.update(lastUsedFilter)}>Apply Last Used Filters</button>
                </div>
              )}
            </UserInfoContext.Consumer>
            <div
              role="button"
              className="filter-close-button"
              onClick={this.toggle}
              onKeyPress={this.toggle}
              aria-label="hide filters"
              tabIndex={-1}
            >
              Close
            </div>
          </div>
        </form>
      </InboxFiltersContainer>
    );
  }
}
